/* eslint-disable react/no-danger */
import { useState, useMemo, useRef, useEffect } from 'react';
import Head from 'next/head';
import Script from 'next/script';
import { useInfiniteScroll, useMemoizedFn, useInViewport, useEventListener, useLockFn } from 'ahooks';
import classNames from 'classnames';
import qs from 'qs';
import { uniqueId } from 'lodash';
import NextLink from 'next/link';
import { MImage as Image } from '@shared/h5/components';
import { parse } from 'node-html-parser';
import Backdrop from '@mui/material/Backdrop';
import pingEvent from '@shared/h5/utils/voyager/pingEvent';
import { getCtx } from '@shared/server/modules/nextjs/ctxBridge';
import conf from '@/conf';
import { Avatar, Author, Layout, Link } from '@/common/components';
import { useAuth } from '@/common/contexts/auth-context';
import {
  getArticleDetail,
  getRelatedArticle,
  getArticleComment,
  // fetchHotCompany,
  getWxConfig,
} from '@/modules/article/models/model';
import Comment from '@/modules/article/components/comment';
import { withAuthServerSideProps, InferWithAuthServerSideProps } from '@/common/utils/with-auth-server-side-props';
import delay from '@/common/utils/delay';
// import formatNumber from '@/common/utils/format-number';
import { OpenAppFromBottom } from '@shared/h5/components/OpenApp';

import styles from '@/modules/article/styles/index.module.scss';

const { Content, Sider } = Layout;

const relatedTabs = ['相关推荐', '最新发布', '大家都在看', '热门人脉圈'];

async function getLoadMoreList(fid: string, nextPage?: number) {
  let page = 1;
  if (nextPage) {
    page = nextPage;
  }

  try {
    const data = await getArticleComment(fid, page);
    return { nextPage: page + 1, list: data.feeds.lst, isNoMore: !data?.feeds?.lst?.length };
  } catch (error) {
    return Promise.reject(error);
  }
}

export const getServerSideProps = withAuthServerSideProps(async (context: GetServerSidePropsContext) => {
  const ctx = await getCtx(context);
  const userAgent = context.req.headers['user-agent'];
  if (!userAgent) {
    return {
      props: { errorMessage: '您无权访问此资源', data: {} },
    };
  }
  const isInWechat = Boolean(userAgent.match(/MicroMessenger/i));
  const query = context.query as Record<string, string | undefined>;

  if (!query.fid || !query.efid) {
    return {
      notFound: true,
    };
  }

  try {
    // SessionPayload.u 兼容 string | number（历史 number + 未来 string），API 入参统一 number
    const sessionU = ctx.session.u != null ? Number(ctx.session.u) : undefined;
    const [{ data }, relatedData] = await Promise.all([
      getArticleDetail(query.fid, query.efid, sessionU, ctx.session.access_token),
      getRelatedArticle(query.fid, query.efid),
      // fetchHotCompany(query.fid),
    ]);

    if (!data) {
      return {
        notFound: true,
      };
    }

    return { props: { data, query, relatedData, isInWechat } };
  } catch (error) {
    if (error.response.data.error_code === 30023) {
      return {
        props: { errorMessage: error.response.data.error_msg, data: {} },
      };
    }
    return {
      notFound: true,
    };
  }
});

export default function Index({
  data,
  relatedData,
  query,
  // hotCompany,
  isInWechat,
  errorMessage,
}: InferWithAuthServerSideProps<typeof getServerSideProps>) {
  const [tabsCurrent, setTabsCurrent] = useState(0);
  const [dialogShow, setDialogShow] = useState(false);
  const { user, isAuth } = useAuth();

  const refList = useRef<Document>(undefined);

  const commentRef = useRef<HTMLDivElement>(null);
  const [inViewport] = useInViewport(commentRef);

  useEffect(() => {
    const node = document.getElementById('js_content');
    // 兜底抓取内容空白问题
    if (node?.style.opacity == '0') {
      node.style.opacity = '1';
    }
  }, []);

  const {
    data: comments,
    loadingMore,
    noMore,
  } = useInfiniteScroll((d) => getLoadMoreList(query.fid, d?.nextPage), {
    manual: !isAuth,
    target: refList,
    reloadDeps: [isAuth],
    isNoMore: (d) => d?.isNoMore,
  });

  useEffect(() => {
    refList.current = document;
  }, []);

  const onWxJsLoad = () => {
    getWxConfig().then((res) => {
      if (window.wx && res?.config) {
        window.wx.config({
          openTagList: ['wx-open-launch-app'],
          ...res?.config,
        });
        window.wx.ready(() => {
          const config = {
            title: data.feed.main?.title || '脉脉专栏文章',
            desc: '快来看看职场最新专栏文章',
            link: window.location.href,
            imgUrl: 'https://i9.taou.com/maimai/c/offlogo/b46c3ee283e39cf855043e342cd01a82.jpeg',
          };
          window.wx.onMenuShareAppMessage(config);
          window.wx.onMenuShareTimeline(config);
          window.wx.onMenuShareQQ(config);
          window.wx.onMenuShareQZone(config);
        });
      }
    });
  };

  // 文章标签
  const tags = useMemo(
    () => (typeof data.helmet_keywords === 'string' ? data.helmet_keywords.split(',') : []),
    [data?.helmet_keywords]
  );

  // 阅读数
  const viewed = useMemo(() => {
    const likes = data.feed?.likes.n + (data.feed?.feeds?.likes || 0);
    return Math.max(data.feed?.views + data.feed?.dviews, likes);
  }, [data?.feed]);

  // 热门人脉
  const relatedCompany = useMemo(() => relatedData?.related_company?.data || [], [relatedData]);

  // 推荐
  const relateds = useMemo(() => {
    let result;
    if (tabsCurrent === 0) {
      result = relatedData?.related_articles.articles;
    }
    if (tabsCurrent === 1) {
      result = relatedData?.new_articles.articles;
    }
    if (tabsCurrent === 2) {
      result = relatedData?.hot_articles.articles;
    }
    if (tabsCurrent === 3) {
      result = relatedData?.related_company.data;
    }
    return result;
  }, [relatedData, tabsCurrent]);

  const innerHTML = useMemo(() => {
    if (!data.feed) return;
    const { html } = data.feed?.main;

    if (!html) return html;

    try {
      const root = parse(html);

      const nodes = root.querySelectorAll('a');
      nodes.map((node) => {
        const href = node.getAttribute('href');
        if (href) {
          const url = new URL(href);
          //  站内
          if (url.host === 'maimai.cn') return null;
          // 针对外链 跳转提示中间页
          const newHref = `${conf.basePath}/link?target=${encodeURIComponent(href)}`;
          node.setAttribute('href', newHref);
          node.setAttribute('target', '_blank');
        }
        return null;
      });

      const imgs = root.querySelectorAll('img');
      imgs.map((img) => img.setAttribute('referrerPolicy', 'no-referrer'));

      return root.toString();
    } catch (error) {
      return html;
    }
  }, [data.feed]);

  const handleAddFriend = useMemoizedFn((dstu: number | string, trackableToken: string, e) => {
    pingEvent('headline_detail.author_info.add_friend.click', {});
    if (!dstu || !trackableToken) return;
    e.stopPropagation();
    const baseUrl = 'https://maimai.cn/profile/detail';
    window.open(`${baseUrl}?dstu=${dstu}&trackable_token=${trackableToken}&from=seo_page`, '_blank');
  });

  const handleAddComment = useLockFn(async () => {
    pingEvent('headline_detail.bottom.comment_box.click', {});
    await delay(200);

    if (isAuth) {
      window.location.href = `//maimai.cn/article/pub_feed_pc?${qs.stringify({
        join_article: query.fid,
        send_headline: true,
      })}`;
    } else {
      const { href } = window.location;
      window.location.href = `//maimai.cn/platform/login?to=${encodeURIComponent(href)}`;
    }
  });

  const handleCloseDialog = () => {
    pingEvent('headline_detail.login_popup.continue_view.click', {});
    setDialogShow(false);
  };

  const login = useLockFn(async () => {
    pingEvent('headline_detail.login_popup.login_or_reg.click', {});
    await delay(200);

    const { href } = window.location;
    window.location.href = `https://maimai.cn/platform/login?to=${encodeURIComponent(href)}`;
  });

  // 登录弹窗曝光埋点
  useEffect(() => {
    if (dialogShow) {
      pingEvent('headline_detail.middle.login_popup.show', {});
    }
  }, [dialogShow]);

  // 评论区曝光埋点
  useEffect(() => {
    if (inViewport) {
      pingEvent('headline_detail.bottom.comment_area.show', {});
    }
  }, [inViewport]);

  useEventListener(
    'scroll',
    () => {
      const isFirst = localStorage.getItem('isFirst');
      // 首次浏览
      if (!isFirst) {
        setDialogShow(true);
        localStorage.setItem('isFirst', '1');
      }
    },
    { once: true }
  );

  if (errorMessage) {
    return (
      <div className='flex flex-col justify-center'>
        <div className='mx-auto mt-14'>
          <Image src='https://i9.taou.com/maimai/p/32612/8848_6_4LgXHL0WKwpwFY' width='70' height='70' />
        </div>

        <div className='mx-auto mt-2 text-neutral-400'>{errorMessage}</div>
      </div>
    );
  }

  return (
    <>
      <Head>
        <title>{data.helmet_title}</title>
        <meta name='keywords' content={data.helmet_keywords} />
        <meta name='description' content={data.helmet_description} />
      </Head>
      {isInWechat && <Script src='//res.wx.qq.com/open/js/jweixin-1.6.0.js' onLoad={onWxJsLoad} />}
      <Layout user={user}>
        <Content>
          <div className='relative mb-20 overflow-hidden rounded-lg bg-white px-6 pt-3 pb-16 md:mb-4 md:px-8 md:pb-3'>
            <div>
              <h1 className='py-8 text-2xl font-medium leading-10 text-[#222222]'>{data.feed.main?.title}</h1>
              {data.is_article ? (
                <div className='flex items-center'>
                  <Author
                    className='flex-1'
                    avatar={
                      <Avatar
                        size='36'
                        contact={data.feed.main.u}
                        onClick={() => {
                          pingEvent('headline_detail.author_info.avatar.click', {});
                        }}
                      />
                    }
                    name={data.feed.main.u.name}
                    description={`${data.feed.time_str} · ${data.feed.main.u.compos}`}
                    judge={data.feed.main.u.judge}
                    positionJudge={data.feed.main.u.position_judge}
                  />

                  {data.feed.main.u.id !== user.id && (
                    <button
                      type='button'
                      className='ml-2 flex items-center rounded-2xl bg-blue-600 px-4 text-center text-xs leading-7 text-white'
                      onClick={(e) => handleAddFriend(data.feed.main.u.id, data.feed.main.u.trackable_token, e)}
                    >
                      <svg
                        height='12'
                        width='12'
                        fill='#fff'
                        viewBox='0 0 32 32'
                        style={{ flex: '0 0 auto', marginRight: '4.5px' }}
                      >
                        <title>add-friend</title>
                        <g id='页面-1' fillRule='evenodd'>
                          <g id='V6-icon合集' transform='translate(-1696 -478)'>
                            <g id='add-friend' transform='translate(1696 478)'>
                              <g id='编组-27' transform='translate(4 4)'>
                                <g id='编组-22备份'>
                                  <path
                                    d='M14.5 13c.087 0 .173.002.258.006A2.99 2.99 0 0 0 14 15v1h-1a3 3 0 0 0-3 3v2l.005.176A3 3 0 0 0 13 24H1.683C.753 24 0 23.247 0 22.317V18.5A5.5 5.5 0 0 1 5.5 13h9zm5.5 5.5v3.817c0 .93-.753 1.683-1.683 1.683H16v-2h-3a1 1 0 0 1-1-1v-2a1 1 0 0 1 1-1h3v-3a1 1 0 0 1 1-1h.664A5.493 5.493 0 0 1 20 18.5zM10 0a4.6 4.6 0 0 1 4.585 4.983l-.253 3.03a4.347 4.347 0 0 1-8.664 0l-.253-3.03A4.6 4.6 0 0 1 10 0z'
                                    id='形状结合'
                                  />
                                </g>
                                <g id='编组-26' transform='translate(12 14)'>
                                  <path
                                    d='M7 0a1 1 0 0 1 1 1v3h3a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H8v3a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8H1a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3V1a1 1 0 0 1 1-1h2z'
                                    id='形状结合'
                                  />
                                </g>
                              </g>
                            </g>
                          </g>
                        </g>
                      </svg>
                      好友
                    </button>
                  )}
                </div>
              ) : (
                <Author
                  avatar={
                    <div className='flex h-9 w-9 flex-none items-center justify-center overflow-hidden rounded-full border border-gray-500 text-center font-medium text-gray-500'>
                      {data.feed.main.source_name?.[0]}
                    </div>
                  }
                  name={data.feed.main.source_name}
                  description={data.feed.time_str}
                />
              )}
            </div>
            <div>
              {/* 文章内容 */}
              <div className='break-words pt-8 text-lg'>
                {data.feed.main.html ? (
                  <div
                    className={classNames(
                      'prose prose-h2:text-[1.35em] max-w-none',
                      data.feed.main.source_type === 2 ? styles.rich_article : styles.article
                    )}
                    dangerouslySetInnerHTML={{ __html: innerHTML }}
                  />
                ) : (
                  <div className='whitespace-pre-wrap text-base text-[#15161F]'>
                    {data.feed.main.rtext || data.feed.main.text}
                  </div>
                )}
              </div>
              {/* 标签 */}
              <div className='py-7 text-xs'>
                {tags?.slice(0, 5).map((tag) => (
                  <span
                    className='my-1 mr-2 inline-block rounded bg-[#F4F5FA] px-2 leading-5 text-[#6E727A] last:mr-0'
                    key={tag}
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <div className='h-0 border-t-2 border-dotted text-center'>
                <span className='relative top-[-14px] bg-white px-1 text-xs font-bold text-[#6E727A] '>END</span>
              </div>
              <div className='py-4 text-sm text-gray-400 after:clear-both after:block'>
                {!data.is_article && (
                  <span>
                    {data.feed.main.source_share ? `${data.feed.main.source_share}人` : ''}从
                    <NextLink href={`/link?target=${encodeURIComponent(data.feed?.hash)}`} legacyBehavior={true}>
                      <a className='cursor-pointer px-1 text-blue-400' target='_blank'>
                        {data.feed.main.source_name}
                      </a>
                    </NextLink>
                    分享到脉脉
                  </span>
                )}
                <span className='float-right '>阅读&nbsp;{viewed || '1'}</span>
              </div>

              <div className='mb-8 text-xs text-[#6E727A]'>
                声明：本文内容由脉脉用户自发贡献，部分内容可能整编自互联网，版权归原作者所有，脉脉不拥有其著作权，亦不承担相应法律责任。如果您发现有涉嫌抄袭的内容，请发邮件至maimai@taou.com，一经查实，将立刻删除涉嫌侵权内容。
              </div>

              {/* 推荐位 */}
              <div>
                <div className='flex overflow-x-auto'>
                  {relatedTabs.map((name, i) => (
                    <div
                      key={name}
                      className={classNames(
                        'last:sider:hidden relative my-3 flex-none cursor-pointer px-2 pb-1 text-lg text-[#6E727A] transition-all',
                        { 'text-black': tabsCurrent === i, 'font-medium': tabsCurrent === i }
                      )}
                      onClick={(e) => {
                        (e.target as HTMLElement)?.scrollIntoView({
                          block: 'nearest',
                          inline: 'center',
                        });
                        setTabsCurrent(i);
                      }}
                    >
                      {name}
                      <div
                        className={classNames(
                          'absolute left-0 bottom-0 h-0.5 w-full scale-0 scale-y-100 bg-black transition-all',
                          {
                            'scale-x-[.3]': tabsCurrent === i,
                          }
                        )}
                      />
                    </div>
                  ))}
                </div>
                <ul className='mb-10 divide-y divide-slate-200/75 rounded-lg bg-gray-100 px-4'>
                  {relateds?.slice(0, 5).map((item) => (
                    <li className='truncate text-sm leading-[46px]' key={uniqueId()}>
                      <Link
                        href={
                          item.ekey
                            ? `/mai/${item.ekey}`
                            : `/article/detail?fid=${item.fid || item.id}&efid=${item.efid}`
                        }
                      >
                        {item.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div ref={commentRef}>
                <div className='hidden pb-3 md:block'>
                  <div className='pb-1 text-lg font-medium text-[#15161F]'>评论</div>
                  <div className='my-4 flex'>
                    <Avatar size='40' contact={{ avatar: user.avatar, mem_id: user.mem_id }} />
                    <div
                      className='ml-2 flex-auto rounded-lg bg-gray-100 px-4 py-2 text-gray-400'
                      onClick={handleAddComment}
                    >
                      我来说几句...
                    </div>
                  </div>
                </div>

                {/* 评论 */}
                {data?.comments?.n ? (
                  <div>
                    <div className='pb-1 text-lg font-medium text-[#15161F]'>全部{data.comments.n}条评论</div>
                    <div className='divide-y divide-slate-100'>
                      {data.comments.lst.map((item) => {
                        if (item.newAdStyle) {
                          return null;
                        }
                        return <Comment data={item} key={item.id} />;
                      })}
                      {comments?.list.map((item) => {
                        if (item.newAdStyle) {
                          return null;
                        }
                        return <Comment data={item} key={item.id} />;
                      })}
                    </div>
                    {loadingMore && <p className='my-4 text-center text-xs text-[#afb1bc]'>正在加载...</p>}
                    {noMore && <p className='my-4 text-center text-xs text-[#afb1bc]'>已显示全部内容</p>}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* h5 */}
          <div className='fixed bottom-0 left-0 right-0 z-10 bg-white px-3 md:hidden'>
            <div className='my-4 flex'>
              <Avatar size='40' contact={{ avatar: user.avatar, mem_id: user.mem_id }} />
              <div className='ml-2 flex-auto rounded-lg bg-gray-100 px-4 py-2 text-gray-400' onClick={handleAddComment}>
                我来说几句...
              </div>
            </div>
          </div>

          <div className='md:hidden'>
            <OpenAppFromBottom
              contentType='www_growth_cb_article'
              clipboardKey={`www_growth_cb_article***${query.fid}***${query.share_uid}`}
              style={{ bottom: '100px' }}
            />
          </div>

          <Backdrop sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }} open={dialogShow}>
            <div className='relative w-10/12 max-w-sm rounded-lg bg-white px-10 py-8 text-[#15161F]'>
              {/* 关闭按钮 */}
              <div className='absolute top-2 right-2 cursor-pointer text-gray-400' onClick={handleCloseDialog}>
                <svg viewBox='0 0 1024 1024' fill='currentColor' width='24' height='24'>
                  <path d='M504.224 470.288l207.84-207.84a16 16 0 0 1 22.608 0l11.328 11.328a16 16 0 0 1 0 22.624l-207.84 207.824 207.84 207.84a16 16 0 0 1 0 22.608l-11.328 11.328a16 16 0 0 1-22.624 0l-207.824-207.84-207.84 207.84a16 16 0 0 1-22.608 0l-11.328-11.328a16 16 0 0 1 0-22.624l207.84-207.824-207.84-207.84a16 16 0 0 1 0-22.608l11.328-11.328a16 16 0 0 1 22.624 0l207.824 207.84z' />
                </svg>
              </div>

              <div className='text-center text-base'>登录查看更多内容、看点评、加好友</div>
              <div className='mt-10 flex justify-center text-center text-sm'>
                <button
                  type='button'
                  className='w-32 rounded-3xl bg-[#383A4C] leading-[44px] text-white'
                  onClick={login}
                >
                  登录 / 注册
                </button>
              </div>
            </div>
          </Backdrop>
        </Content>

        {relatedCompany?.length ? (
          <Sider>
            {Boolean(relatedCompany?.length) && (
              <div className='rounded-lg bg-white px-6 py-2 text-[#15161F]'>
                <div className='text-base font-medium leading-10'>热门人脉圈</div>
                <ul>
                  {relatedCompany.slice(0, 5).map((item) => (
                    <li className='cursor-pointer truncate text-sm leading-10' key={item.ekey}>
                      <Link href={`/mai/${item.ekey}`}>{item.title}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* {Boolean(hotCompany?.length) && (
              <div className='mt-4 text-[#15161F] first:mt-0'>
                <div className='px-4 text-base font-medium leading-10'>热门公司</div>
                <ul className='space-y-2'>
                  {hotCompany.map((item) => (
                    <li className='flex px-4 py-3 bg-white rounded-lg' key={item.cid}>
                      <div className='flex-none w-10 h-10 mr-3 overflow-hidden border rounded'>
                        <Link href={`/brand/home/${item.webcid}`}>
                          <Image
                            src={item.clogo || 'https://i9.taou.com/maimai/c/offlogo/default.png'}
                            alt={`${item.name}公司logo`}
                            width='40'
                            height='40'
                          />
                        </Link>
                      </div>

                      <div className='flex flex-col justify-between flex-1 overflow-hidden'>
                        <div className='flex justify-between overflow-hidden text-sm font-medium'>
                          <Link className='truncate' href={`/brand/home/${item.webcid}`}>
                            {item.name}
                          </Link>
                          <div className='flex-none ml-1 text-xs'>
                            <Link href={`/brand/review/${item.webcid}`}>看点评</Link>
                            {Boolean(item.job_cnt && item.user_cnt) && '｜'}
                            {Boolean(item.job_cnt) && <Link href={`/brand/recruit/${item.webcid}`}>找工作</Link>}
                          </div>
                        </div>

                        <div className='text-xs text-gray-400'>
                          {Boolean(item.job_cnt) && (
                            <>
                              <span className='text-[#15161F]'>{formatNumber(item.job_cnt)}</span> 职位{' '}
                            </>
                          )}
                          <span className='text-[#15161F]'>{formatNumber(item.user_cnt)}</span>
                          员工在脉脉
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )} */}
          </Sider>
        ) : null}
      </Layout>
    </>
  );
}
